import DeleteConfirmPopover from "components/common/DeleteConfirmPopover";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  notify,
  RefreshButton,
  Spinner,
  Text,
  VStack,
} from "components/ui";
import { useAutonomousAgents } from "hooks/useAutonomousAgents";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdAdd, MdArrowBack, MdBolt, MdChevronRight } from "react-icons/md";
import {
  AutonomousAgent,
  autonomousAgentService,
  AutonomousExecutionMode,
} from "services/autonomousAgentService";
import { WidgetConfig, widgetService } from "services/widgetService";
import { getThemeColor } from "theme/colors";

import AccessTokensPanel from "./AccessTokensPanel";
import AutonomousAgentForm from "./AutonomousAgentForm";
import ExecutionLogsPanel from "./ExecutionLogsPanel";
import WebhookPanel from "./WebhookPanel";

type AreaTab = "agents" | "tokens";
type PanelMode = "none" | "create" | "edit";

const AutonomousAgents = () => {
  const { t } = useTranslation();
  const { agents, loading, reload } = useAutonomousAgents();
  const [tab, setTab] = useState<AreaTab>("agents");
  const [widgets, setWidgets] = useState<WidgetConfig[]>([]);
  const [panelMode, setPanelMode] = useState<PanelMode>("none");
  const [selectedAgent, setSelectedAgent] = useState<AutonomousAgent | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  // Unsaved Response-mode pick from the form — lets the webhook recipe below
  // react immediately (e.g. hide the async-only "Get the result" step).
  const [draftMode, setDraftMode] = useState<AutonomousExecutionMode | null>(
    null,
  );

  useEffect(() => {
    widgetService
      .getAllWidgets()
      .then(setWidgets)
      .catch(() => setWidgets([]));
  }, []);

  const startCreate = () => {
    setSelectedAgent(null);
    setDraftMode(null);
    setPanelMode("create");
  };

  const openAgent = (agent: AutonomousAgent) => {
    setSelectedAgent(agent);
    setDraftMode(null);
    setPanelMode("edit");
  };

  const backToList = () => {
    setSelectedAgent(null);
    setDraftMode(null);
    setPanelMode("none");
  };

  const handleSaved = async (agent: AutonomousAgent) => {
    await reload();
    setSelectedAgent(agent);
    setDraftMode(null); // saved agent is authoritative again
    setPanelMode("edit");
  };

  const handleDelete = async () => {
    if (!selectedAgent) return;
    setDeleting(true);
    try {
      await autonomousAgentService.remove(selectedAgent.id);
      notify({
        type: "success",
        title: t("app.autonomousAgents.notifications.deleted"),
      });
      backToList();
      await reload();
    } finally {
      setDeleting(false);
    }
  };

  const renderAgentCard = (agent: AutonomousAgent) => (
    <Box
      key={agent.id}
      onClick={() => openAgent(agent)}
      cursor="pointer"
      px={4}
      py={4}
      borderRadius="md"
      borderWidth="1px"
      borderColor={getThemeColor("border.default")}
      _hover={{
        borderColor: getThemeColor("text.link"),
        bg: getThemeColor("sidebar.hoverBg"),
      }}
      transition="border-color 0.15s, background 0.15s"
    >
      <Flex justify="space-between" align="center" gap={2}>
        <VStack align="start" gap={1} overflow="hidden">
          <Text fontSize="sm" fontWeight="semibold" lineClamp={1}>
            {agent.name}
          </Text>
          <HStack gap={2}>
            <Badge colorPalette={agent.isActive ? "green" : "gray"} size="sm">
              {agent.isActive
                ? t("app.autonomousAgents.status.active")
                : t("app.autonomousAgents.status.disabled")}
            </Badge>
            <Text fontSize="xs" color={getThemeColor("text.secondary")}>
              {t("app.autonomousAgents.runsCount", {
                count: agent.executionCount,
              })}
            </Text>
          </HStack>
        </VStack>
        <Box color={getThemeColor("text.secondary")} flexShrink={0}>
          <MdChevronRight size={20} />
        </Box>
      </Flex>
    </Box>
  );

  const renderList = () => (
    <Card.Root w="full">
      <Card.Body>
        <VStack align="stretch" gap={4}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <Heading size="sm">{t("app.autonomousAgents.listTitle")}</Heading>
            <HStack gap={2}>
              <RefreshButton onClick={reload} loading={loading} iconOnly />
              <Button intent="primary" size="sm" onClick={startCreate}>
                <MdAdd />
                {t("app.autonomousAgents.newAgent")}
              </Button>
            </HStack>
          </Flex>

          {loading ? (
            <Flex justify="center" py={10}>
              <Spinner />
            </Flex>
          ) : agents.length === 0 ? (
            <Flex align="center" justify="center" py={12}>
              <VStack gap={3}>
                <MdBolt size={36} color="gray" />
                <Text
                  fontSize="sm"
                  color={getThemeColor("text.secondary")}
                  textAlign="center"
                >
                  {t("app.autonomousAgents.empty")}
                </Text>
                <Button intent="primary" size="sm" onClick={startCreate}>
                  <MdAdd />
                  {t("app.autonomousAgents.create")}
                </Button>
              </VStack>
            </Flex>
          ) : (
            <Box
              display="grid"
              gap={3}
              gridTemplateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                xl: "repeat(3, 1fr)",
              }}
            >
              {agents.map(renderAgentCard)}
            </Box>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  );

  const renderEditor = () => (
    <VStack align="stretch" gap={4} w="full">
      <Flex justify="space-between" align="center" gap={3} wrap="wrap">
        <Button intent="secondary" size="sm" onClick={backToList}>
          <MdArrowBack />
          {t("app.autonomousAgents.backToList")}
        </Button>
        {panelMode === "edit" && selectedAgent && (
          <DeleteConfirmPopover
            onConfirm={handleDelete}
            loading={deleting}
            title={t("app.autonomousAgents.delete")}
            message={t("app.autonomousAgents.deleteConfirm")}
            triggerLabel={t("app.autonomousAgents.delete")}
          />
        )}
      </Flex>

      <Card.Root w="full">
        <Card.Body>
          <VStack align="stretch" gap={5}>
            <Heading size="md">
              {panelMode === "create"
                ? t("app.autonomousAgents.create")
                : selectedAgent?.name}
            </Heading>

            <AutonomousAgentForm
              agent={panelMode === "edit" ? selectedAgent : null}
              widgets={widgets}
              onSaved={handleSaved}
              onExecutionModeChange={setDraftMode}
            />

            {panelMode === "edit" && selectedAgent && (
              <>
                <Box
                  borderTopWidth="1px"
                  borderColor={getThemeColor("border.default")}
                  pt={4}
                >
                  <WebhookPanel
                    agent={
                      draftMode
                        ? { ...selectedAgent, executionMode: draftMode }
                        : selectedAgent
                    }
                  />
                </Box>
                <Box
                  borderTopWidth="1px"
                  borderColor={getThemeColor("border.default")}
                  pt={4}
                >
                  <ExecutionLogsPanel agentId={selectedAgent.id} />
                </Box>
              </>
            )}
          </VStack>
        </Card.Body>
      </Card.Root>
    </VStack>
  );

  return (
    <VStack align="stretch" gap={5} w="full">
      {/* Header */}
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
        <Box>
          <Heading size="lg">{t("app.autonomousAgents.title")}</Heading>
          <Text color={getThemeColor("text.secondary")}>
            {t("app.autonomousAgents.subtitle")}
          </Text>
        </Box>
      </Flex>

      {/* Area toggle */}
      <HStack gap={2}>
        <Button
          intent={tab === "agents" ? "primary" : "secondary"}
          size="sm"
          onClick={() => setTab("agents")}
        >
          {t("app.autonomousAgents.tabs.agents")}
        </Button>
        <Button
          intent={tab === "tokens" ? "primary" : "secondary"}
          size="sm"
          onClick={() => setTab("tokens")}
        >
          {t("app.autonomousAgents.tabs.accessTokens")}
        </Button>
      </HStack>

      {tab === "tokens" ? (
        <AccessTokensPanel />
      ) : panelMode === "none" ? (
        renderList()
      ) : (
        renderEditor()
      )}
    </VStack>
  );
};

export default AutonomousAgents;
